/* eslint-disable import/no-duplicates */
import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import {
  Text,
  Button,
  Icon,
  useBreakpointValue,
  useDisclosure,
  ModalBody,
  ModalFooter,
  Box,
  Flex,
  VStack,
  HStack,
  Badge,
  IconButton,
  Divider,
  Switch,
  Input as ChakraInput,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import {
  RiAddLine,
  RiDeleteBinLine,
  RiUserLine,
  RiCalendarLine,
  RiMapPinLine,
  RiInformationLine,
  RiFileTextLine,
  RiMoneyDollarCircleLine,
  RiFileUploadLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiCheckLine,
  RiPieChartLine,
  RiCheckboxCircleFill,
} from 'react-icons/ri';
import { useLoading, Oval } from '@agney/react-loading';
import { v4 as uuid } from 'uuid';
import { apllyToast } from '../../../Global/Toast2.0';
import getValidationErrors from '../../../../utils/getValidationErrors';
import { roundKm } from '../../../../utils/roundKm';
import {
  formatReportDate,
  isValidReportDate,
  maxReportDate,
  MIN_REPORT_DATE,
} from '../../../../utils/formatReportDate';
import { useAuth } from '../../../../hooks/auth';
import {
  IExpenseReportDraftData,
  saveExpenseReportDraft,
  loadExpenseReportDraft,
  clearExpenseReportDraft,
  isDraftEmpty,
} from '../../../../utils/expenseReportDraft';

import Input from '../../../Global/Input';
import TextArea from '../../../Global/TextArea';
import Select from '../../../Global/SelectRelease';
import { UserSelect } from '../../../Global/UserSelect';
import { UploadSimulated } from '../../../Global/UploadSimulated';
import { Alert } from '../../Alert';
import {
  ExpenseParticipants,
  createEmptyParticipant,
} from '../../Miles/ExpenseParticipants';

import {
  ExpenseRateio,
  computeRateioDistributed,
  IRateioItem,
} from '../../Miles/ExpenseRateio';
import { SearchableSelect } from '../../Miles/SearchableSelect';
import { COST_CENTERS } from '../../../../constants/orgStructure';

import { ModalComponent } from '..';

import {
  IExpenseReport,
  IExpenseItem,
  IExpenseParticipant,
  IExpenseRateio,
  isDirectorSector,
  isFixedRateEligibleUser,
  isAjudaCustoReport,
  SPECIAL_SECTOR_RATE,
  getKmRate,
  recalculateKmGroupItems,
} from '../../../../pages/Dashboard/vehicles_management/Expenses';
import {
  usersService,
  IUserOption,
  IBranchOption,
} from '../../../../services/usersService';

interface IModalProps {
  handleAddExpense: (
    expense: Omit<IExpenseReport, 'id' | 'created_at' | 'updated_at'>,
  ) => void;
}

// Schema de validação para dados do relatório
const reportSchema = Yup.object().shape({
  date: Yup.string()
    .required('Data é obrigatória')
    .test('data-valida', 'Informe uma data válida', value =>
      isValidReportDate(value),
    ),
});

// Schema de validação para item de despesa
const getItemValidationSchema = (expenseType: string) => {
  const baseSchema: any = {
    expense_type: Yup.string().required('Tipo de despesa é obrigatório'),
    observations: Yup.string()
      .required('Observação é obrigatória')
      .min(5, 'Descreva com pelo menos 5 caracteres')
      .max(2000, 'Máximo de 2000 caracteres'),
  };

  switch (expenseType) {
    case 'Quilometragem adicional':
      return Yup.object().shape({
        ...baseSchema,
        displacement_reason: Yup.string()
          .required('Motivo do deslocamento é obrigatório')
          .min(10, 'Descreva o motivo com pelo menos 10 caracteres')
          .max(500, 'Máximo de 500 caracteres'),
      });

    case 'Refeição':
      return Yup.object().shape({
        ...baseSchema,
        amount: Yup.number()
          .required('Valor é obrigatório')
          .min(0.01, 'Valor deve ser maior que zero'),
        receipt: Yup.mixed().required('Comprovante é obrigatório'),
      });

    case 'Pedágio adicional':
      return Yup.object().shape({
        ...baseSchema,
        amount: Yup.number()
          .required('Valor é obrigatório')
          .min(0.01, 'Valor deve ser maior que zero'),
        receipt: Yup.mixed().required('Comprovante é obrigatório'),
      });

    case 'Outros':
      return Yup.object().shape({
        ...baseSchema,
        amount: Yup.number()
          .required('Valor é obrigatório')
          .min(0.01, 'Valor deve ser maior que zero'),
        receipt: Yup.mixed().required('Comprovante é obrigatório'),
      });

    case 'Ajuda de custo':
      return Yup.object().shape({
        expense_type: Yup.string().required('Tipo de despesa é obrigatório'),
        amount: Yup.number()
          .required('Valor é obrigatório')
          .min(0.01, 'Valor deve ser maior que zero'),
        // Comprovante não é obrigatório para Ajuda de custo.
        receipt: Yup.mixed(),
        observations: Yup.string().required('Observação é obrigatória'),
      });

    default:
      return Yup.object().shape(baseSchema);
  }
};

const getExpenseColor = (type: string): string => {
  if (type === 'Refeição') return 'green';
  if (type === 'Quilometragem adicional') return 'blue';
  if (type === 'Pedágio adicional') return 'orange';
  if (type === 'Ajuda de custo') return 'teal';
  return 'purple';
};

// Tipos de despesa cujo comprovante é obrigatório (ver getItemValidationSchema).
// Usado para bloquear o envio quando um item restaurado de um rascunho ainda
// não teve o comprovante reanexado.
const RECEIPT_REQUIRED_TYPES = ['Refeição', 'Pedágio adicional', 'Outros'];

// Item de despesa em memória no wizard: igual a IExpenseItem, mas com marcas
// opcionais de "comprovante pendente" quando o item veio de um rascunho
// restaurado (o arquivo original não pôde ser salvo no localStorage).
type IWizardExpenseItem = IExpenseItem & {
  receiptPending?: boolean;
  receiptFileName?: string;
};

export function ModalAddNewExpense({ handleAddExpense }: IModalProps) {
  const formRef = useRef<FormHandles>(null);
  const itemFormRef = useRef<FormHandles>(null);
  const { onOpen, isOpen, onClose } = useDisclosure();
  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true,
  });

  const componentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  // Etapa atual do wizard (0..3)
  const [step, setStep] = useState(0);

  // Estados do relatório
  const [reportDate, setReportDate] = useState('');
  const [managerId, setManagerId] = useState('');
  const [users, setUsers] = useState<IUserOption[]>([]);

  const [branches, setBranches] = useState<IBranchOption[]>([]);

  // Participantes (responsável + vinculados) e rateio
  const [participants, setParticipants] = useState<IExpenseParticipant[]>([
    createEmptyParticipant('responsible'),
  ]);
  const [rateio, setRateio] = useState<IExpenseRateio>({
    mode: 'percentual',
    allocations: [],
  });

  // Participante responsável (índice base do relatório)
  const responsible =
    participants.find(p => p.role === 'responsible') || participants[0];
  const director = responsible?.user_id || '';

  // Carregar usuários e filiais ao abrir modal
  useEffect(() => {
    if (isOpen) {
      usersService
        .list()
        .then(data => setUsers(data))
        .catch(err => console.error('Erro ao carregar usuários:', err));
      usersService
        .listBranches()
        .then(data => setBranches(data))
        .catch(err => console.error('Erro ao carregar filiais:', err));
    }
  }, [isOpen]);

  // Flag de cálculo KM — diretoria (por setor) ou usuários elegíveis (por ID) podem escolher taxa fixa
  const [useDirectorKmRate, setUseDirectorKmRate] = useState(false);
  const selectedUserSectorId = users.find(u => u.id === director)?.sector_id;
  const isDirectorUser = isDirectorSector(selectedUserSectorId);
  const canUseFixedKmRate = isDirectorUser || isFixedRateEligibleUser(director);

  // Estados de KM (hodômetro)
  const [kmStart, setKmStart] = useState('');
  const [kmEnd, setKmEnd] = useState('');
  const [kmStartPhoto, setKmStartPhoto] = useState<File | null>(null);
  const [kmEndPhoto, setKmEndPhoto] = useState<File | null>(null);
  // O hodômetro marca décimos (11306.7) e é isso que o usuário digita, mas
  // km_start/km_end/additional_km são colunas `integer` no banco: mandar o
  // decimal quebrava o INSERT e voltava como "Internal server error".
  // Arredondamos aqui, no mesmo lugar em que o valor é exibido, para o que
  // aparece na tela ser exatamente o que é gravado.
  const kmStartValue = roundKm(kmStart);
  const kmEndValue = roundKm(kmEnd);
  const calculatedKm =
    kmStart && kmEnd ? kmEndValue - kmStartValue : 0;
  const kmHasDecimals =
    (!!kmStart && Number(kmStart) !== kmStartValue) ||
    (!!kmEnd && Number(kmEnd) !== kmEndValue);

  // Data de pagamento desejada da Ajuda de custo — quem solicita escolhe,
  // pois só ela sabe quando precisa do valor (ex: mesma semana de um curso).
  const [ajudaCustoDueDate, setAjudaCustoDueDate] = useState('');

  // Estados dos itens
  const [items, setItems] = useState<IWizardExpenseItem[]>([]);
  const [currentItemType, setCurrentItemType] = useState('');

  // Rascunho local (localStorage) — permite retomar o relatório se o modal
  // fechar/recarregar antes de salvar. Chave por usuário logado.
  const { user } = useAuth();
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<IExpenseReportDraftData | null>(
    null,
  );
  const draftSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Centro de custo POR despesa (conveniência que alimenta o rateio modo 'itens')
  const [costCenterPerItem, setCostCenterPerItem] = useState(false);
  const [itemCostCenters, setItemCostCenters] = useState<
    Record<string, string>
  >({});
  const costCenterOptions = COST_CENTERS.map(c => ({
    value: c.code,
    label: `${c.code} – ${c.name}`,
  }));

  const handleTogglePerItem = useCallback((checked: boolean) => {
    setCostCenterPerItem(checked);
    if (!checked) {
      setItemCostCenters({});
    }
  }, []);

  const handleItemCostCenterChange = useCallback(
    (itemId: string, value: string) => {
      setItemCostCenters(prev => ({ ...prev, [itemId]: value }));
    },
    [],
  );

  // Estados de visibilidade condicional para item
  const [showAmountField, setShowAmountField] = useState(false);
  const [showKmFields, setShowKmFields] = useState(false);
  const [showReceiptField, setShowReceiptField] = useState(false);
  const [showObservationsField, setShowObservationsField] = useState(false);
  const [showAjudaCustoFields, setShowAjudaCustoFields] = useState(false);
  const [eventSchedules, setEventSchedules] = useState<{ id: string }[]>([{ id: uuid() }]);

  // Controlar visibilidade de campos condicionais do item
  useEffect(() => {
    switch (currentItemType) {
      case 'Ajuda de custo':
        setShowKmFields(false);
        setShowAmountField(true);
        setShowReceiptField(true);
        setShowObservationsField(false);
        setShowAjudaCustoFields(true);
        break;

      case 'Quilometragem adicional':
        setShowKmFields(true);
        setShowAmountField(false);
        setShowReceiptField(false);
        setShowObservationsField(true);
        setShowAjudaCustoFields(false);
        break;

      case 'Refeição':
      case 'Pedágio adicional':
      case 'Outros':
        setShowKmFields(false);
        setShowAmountField(true);
        setShowReceiptField(true);
        setShowObservationsField(true);
        setShowAjudaCustoFields(false);
        break;

      default:
        setShowKmFields(false);
        setShowAmountField(false);
        setShowReceiptField(false);
        setShowObservationsField(false);
        setShowAjudaCustoFields(false);
    }
  }, [currentItemType]);

  const handleItemTypeChange = useCallback((value: string) => {
    if (value === 'Ajuda de custo' && items.length > 0) {
      apllyToast('warning', 'Ajuda de custo deve ser a única despesa adicionada neste relatório.');
      return;
    }
    if (value !== 'Ajuda de custo' && items.some(i => i.expense_type === 'Ajuda de custo')) {
      apllyToast('warning', 'Uma Ajuda de custo não pode ser combinada com outras despesas neste relatório.');
      return;
    }
    setCurrentItemType(value);
    // Limpar campos ao trocar tipo
    itemFormRef.current?.setFieldValue('item_amount', '');
    itemFormRef.current?.setFieldValue('item_additional_km', '');
    itemFormRef.current?.setFieldValue('item_displacement_reason', '');
    itemFormRef.current?.setFieldValue('item_receipt', null);
    itemFormRef.current?.setFieldValue('item_observations', '');
    setEventSchedules([{ id: uuid() }]);
    if (value !== 'Ajuda de custo') {
      setAjudaCustoDueDate('');
    }
  }, [items]);

  const handleAddItem = useCallback(
    async (data: any) => {
      try {
        itemFormRef.current?.setErrors({});

        let finalObservations = data.item_observations;
        if (currentItemType === 'Ajuda de custo') {
          if (!ajudaCustoDueDate) {
            apllyToast(
              'warning',
              'Informe a data de pagamento desejada da ajuda de custo.',
            );
            scrollToField('ajuda_custo_data_pagamento');
            return;
          }
          if (!data.ajuda_custo_descricao_valor?.trim()) {
            apllyToast(
              'warning',
              'Informe a descrição de valores (para que a ajuda de custo será utilizada).',
            );
            scrollToField('ajuda_custo_descricao_valor');
            return;
          }

          const ajudaData = {
            nome: data.ajuda_custo_nome,
            objetivo: data.ajuda_custo_objetivo,
            local_evento: data.ajuda_custo_local,
            endereco: data.ajuda_custo_endereco,
            periodo_evento: data.ajuda_custo_periodo,
            horarios: eventSchedules.map((_, index) => ({
              inicio: data[`ajuda_custo_horario_inicio_${index}`],
              termino: data[`ajuda_custo_horario_termino_${index}`]
            })).filter(h => h.inicio || h.termino),
            dados_viagem_data: data.ajuda_custo_data_viagem,
            trecho_ida: data.ajuda_custo_trecho_ida,
            trecho_ida_data: data.ajuda_custo_trecho_ida_data,
            trecho_volta: data.ajuda_custo_trecho_volta,
            trecho_volta_data: data.ajuda_custo_trecho_volta_data,
            descricao_valor: data.ajuda_custo_descricao_valor,
          };
          finalObservations = JSON.stringify(ajudaData);
        }

        // Validação dinâmica baseada no tipo
        const schema = getItemValidationSchema(currentItemType);
        await schema.validate(
          {
            expense_type: currentItemType,
            amount: data.item_amount,
            additional_km: data.item_additional_km,
            displacement_reason: data.item_displacement_reason,
            receipt: data.item_receipt,
            observations: finalObservations,
          },
          { abortEarly: false },
        );

        // Formatar item conforme o tipo
        const newItem: IExpenseItem = {
          id: uuid(),
          expense_type: currentItemType as IExpenseItem['expense_type'],
          observations: finalObservations || undefined,
        };

        if (currentItemType === 'Quilometragem adicional') {
          // Calcular KM diretamente dos estados (já normalizados para inteiro
          // — ver roundKm: as colunas de KM no banco são `integer`)
          const kmStartVal = kmStartValue;
          const kmEndVal = kmEndValue;
          const km = kmEndVal - kmStartVal;

          // Validar KM
          if (!kmStart || !kmEnd || km <= 0) {
            apllyToast(
              'warning',
              'Informe o KM Inicial e Final. O KM Final deve ser maior.',
            );
            return;
          }

          const rate =
            canUseFixedKmRate && useDirectorKmRate
              ? SPECIAL_SECTOR_RATE
              : getKmRate(km, selectedUserSectorId);

          newItem.additional_km = km;
          newItem.amount = Number((km * rate).toFixed(2));
          newItem.km_start = kmStartVal;
          newItem.km_end = kmEndVal;
          newItem.displacement_reason = data.item_displacement_reason;
          newItem.receipt_km_start = kmStartPhoto || undefined;
          newItem.receipt_km_end = kmEndPhoto || undefined;

          // Observations: salvar taxa e tipo apenas quando relevante
          const isUsingFixedRate = canUseFixedKmRate && useDirectorKmRate;
          const kmObs: any = {
            km_rate: rate,
            obs: finalObservations || '',
          };
          if (isUsingFixedRate) {
            kmObs.km_rate_type = isDirectorUser ? 'diretoria' : 'fixo';
          }
          newItem.observations = JSON.stringify(kmObs);
        } else {
          newItem.amount = Number(data.item_amount);
          newItem.receipt = data.item_receipt;
        }

        setItems(prev =>
          recalculateKmGroupItems([...prev, newItem], selectedUserSectorId),
        );
        apllyToast('success', 'Item adicionado!');

        // Resetar formulário do item
        setCurrentItemType('');
        setKmStart('');
        setKmEnd('');
        setKmStartPhoto(null);
        setKmEndPhoto(null);
        setUseDirectorKmRate(false);
        itemFormRef.current?.reset();
        setEventSchedules([{ id: uuid() }]);
        // ajudaCustoDueDate não é resetado aqui: é um dado do relatório (não
        // do item) e ainda será usado ao montar o payload em handleSubmit.
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          itemFormRef.current?.setErrors(errors);
          // Scroll ao primeiro campo com erro do item
          const fieldMap: Record<string, string> = {
            observations: 'item_observations',
            amount: 'item_amount',
            additional_km: 'item_additional_km',
            displacement_reason: 'item_displacement_reason',
            receipt: 'item_receipt',
          };
          const firstKey = Object.keys(errors)[0];
          if (firstKey) {
            scrollToField(fieldMap[firstKey] || firstKey);
            apllyToast('warning', Object.values(errors)[0]);
          }
        } else {
          apllyToast('warning', 'Erro ao adicionar item!');
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentItemType, eventSchedules, isDirectorUser, canUseFixedKmRate, useDirectorKmRate, calculatedKm, kmStart, kmEnd, kmStartPhoto, kmEndPhoto, selectedUserSectorId, ajudaCustoDueDate],
  );

  const handleRemoveItem = useCallback(
    (id: string) => {
      setItems(prev =>
        recalculateKmGroupItems(
          prev.filter(item => item.id !== id),
          selectedUserSectorId,
        ),
      );
      setItemCostCenters(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      apllyToast('info', 'Item removido!');
    },
    [selectedUserSectorId],
  );

  // Reseta todo o estado do wizard. Usado tanto ao cancelar/fechar quanto
  // após salvar com sucesso — sem isso, reabrir o modal para lançar um novo
  // relatório mantinha os dados do usuário/relatório anterior.
  const resetForm = useCallback(() => {
    setStep(0);
    setReportDate('');
    setManagerId('');
    setParticipants([createEmptyParticipant('responsible')]);
    setRateio({ mode: 'percentual', allocations: [] });
    setItems([]);
    setCostCenterPerItem(false);
    setItemCostCenters({});
    setCurrentItemType('');
    setKmStart('');
    setKmEnd('');
    setKmStartPhoto(null);
    setKmEndPhoto(null);
    setUseDirectorKmRate(false);
    setAjudaCustoDueDate('');
    setEventSchedules([{ id: uuid() }]);
    formRef.current?.reset();
    itemFormRef.current?.reset();
  }, []);

  // Ao abrir o modal, verifica se há um rascunho salvo deste usuário e, se
  // houver (e não estiver vazio), pergunta se ele quer continuar de onde
  // parou. Só depende de isOpen/user — não roda de novo a cada tecla digitada.
  useEffect(() => {
    if (isOpen && user?.id) {
      const draft = loadExpenseReportDraft(user.id);
      if (draft && !isDraftEmpty(draft)) {
        setPendingDraft(draft);
        setShowDraftPrompt(true);
      }
    } else if (!isOpen) {
      setShowDraftPrompt(false);
      setPendingDraft(null);
    }
  }, [isOpen, user?.id]);

  // Grava o rascunho imediatamente (sem debounce) com o estado atual. Usado
  // pelo autosave (dentro do setTimeout), pelo fechamento explícito do modal
  // e pelo beforeunload — sempre com os MESMOS dados "ao vivo" via closure,
  // nunca esperando o timer, para não perder a última edição feita bem antes
  // de fechar/recarregar.
  const flushDraftSave = useCallback(() => {
    if (!isOpen || showDraftPrompt || !user?.id) return;
    const draftData: IExpenseReportDraftData = {
      step,
      reportDate,
      managerId,
      participants,
      rateio,
      items,
      costCenterPerItem,
      itemCostCenters,
      ajudaCustoDueDate,
      useDirectorKmRate,
      kmStart,
      kmEnd,
    };
    if (!isDraftEmpty(draftData)) {
      saveExpenseReportDraft(user.id, draftData);
    }
  }, [
    isOpen,
    showDraftPrompt,
    user?.id,
    step,
    reportDate,
    managerId,
    participants,
    rateio,
    items,
    costCenterPerItem,
    itemCostCenters,
    ajudaCustoDueDate,
    useDirectorKmRate,
    kmStart,
    kmEnd,
  ]);

  // Salva o progresso automaticamente (com debounce) enquanto o usuário
  // preenche o relatório. Fica pausado enquanto a pergunta de "retomar
  // rascunho" está na tela, para não sobrescrever o rascunho salvo com o
  // estado ainda vazio do formulário antes de o usuário decidir.
  useEffect(() => {
    if (!isOpen || showDraftPrompt || !user?.id) {
      return undefined;
    }

    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }
    draftSaveTimeoutRef.current = setTimeout(flushDraftSave, 500);

    return () => {
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
      }
    };
  }, [flushDraftSave, isOpen, showDraftPrompt, user?.id]);

  // Fechar a aba/recarregar a página não dá tempo do debounce de 500ms
  // rodar — grava o rascunho na hora se isso acontecer no meio de uma edição.
  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('beforeunload', flushDraftSave);
    return () => {
      window.removeEventListener('beforeunload', flushDraftSave);
    };
  }, [isOpen, flushDraftSave]);

  // Aplica o rascunho pendente ao estado do wizard e fecha a pergunta.
  const handleContinueDraft = useCallback(() => {
    if (!pendingDraft) return;
    setStep(pendingDraft.step || 0);
    setReportDate(pendingDraft.reportDate || '');
    setManagerId(pendingDraft.managerId || '');
    setParticipants(
      pendingDraft.participants?.length
        ? pendingDraft.participants
        : [createEmptyParticipant('responsible')],
    );
    setRateio(pendingDraft.rateio || { mode: 'percentual', allocations: [] });
    setItems((pendingDraft.items || []) as IWizardExpenseItem[]);
    setCostCenterPerItem(!!pendingDraft.costCenterPerItem);
    setItemCostCenters(pendingDraft.itemCostCenters || {});
    setAjudaCustoDueDate(pendingDraft.ajudaCustoDueDate || '');
    setUseDirectorKmRate(!!pendingDraft.useDirectorKmRate);
    setKmStart(pendingDraft.kmStart || '');
    setKmEnd(pendingDraft.kmEnd || '');
    setShowDraftPrompt(false);
    setPendingDraft(null);
  }, [pendingDraft]);

  // Descarta o rascunho salvo — o modal permanece com o formulário em branco.
  const handleDiscardDraft = useCallback(() => {
    if (user?.id) {
      clearExpenseReportDraft(user.id);
    }
    setShowDraftPrompt(false);
    setPendingDraft(null);
  }, [user?.id]);

  // Reanexa o comprovante de um item restaurado de um rascunho (o arquivo
  // original não pôde ser persistido no localStorage).
  const handleReattachReceipt = useCallback((itemId: string, file: File | null) => {
    if (!file) return;
    setItems(prev =>
      prev.map(it =>
        it.id === itemId
          ? { ...it, receipt: file, receiptPending: false, receiptFileName: undefined }
          : it,
      ),
    );
  }, []);

  // Scroll e destaque no campo com erro
  const scrollToField = (fieldName: string) => {
    const modalBody = document.querySelector('.chakra-modal__body');
    // Busca por name, id, ou data-field
    const el =
      modalBody?.querySelector(`[name="${fieldName}"]`) ||
      modalBody?.querySelector(`[data-field="${fieldName}"]`) ||
      modalBody?.querySelector(`#${fieldName}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Destacar brevemente com borda vermelha
      const htmlEl = el as HTMLElement;
      const originalBorder = htmlEl.style.border;
      const originalShadow = htmlEl.style.boxShadow;
      htmlEl.style.border = '2px solid #e53e3e';
      htmlEl.style.boxShadow = '0 0 0 3px rgba(229, 62, 62, 0.3)';
      setTimeout(() => {
        htmlEl.style.border = originalBorder;
        htmlEl.style.boxShadow = originalShadow;
      }, 3000);
    }
  };

  // Total das despesas e itens simplificados para o rateio
  const expensesTotal = items.reduce((sum, i) => sum + (i.amount || 0), 0);
  const rateioItems: IRateioItem[] = items.map(i => ({
    id: i.id,
    label:
      i.expense_type === 'Quilometragem adicional'
        ? `${i.expense_type} (${i.additional_km || 0} km)`
        : i.expense_type,
    value: i.amount || 0,
  }));

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        // No wizard, só finalizamos a partir da última etapa (Revisão).
        // Evita que um Enter acidental em campos anteriores dispare o envio.
        if (steps[step]?.id !== 'review') {
          return;
        }

        setLoading(true);
        formRef.current?.setErrors({});

        // Validar participante responsável
        if (!responsible || !responsible.user_id) {
          apllyToast('warning', 'Selecione o usuário responsável!');
          scrollToField('participants');
          setLoading(false);
          return;
        }

        // Setor/Seção/Centro de Custo são obrigatórios para todos os participantes
        const incomplete = participants.find(
          p =>
            !p.user_id ||
            !p.sector?.trim() ||
            !p.section?.trim() ||
            !p.cost_center?.trim(),
        );
        if (incomplete) {
          apllyToast(
            'warning',
            'Preencha usuário, setor, seção e centro de custo de todos os participantes!',
          );
          scrollToField('participants');
          setLoading(false);
          return;
        }

        // Validar gestor
        if (!isDirectorUser && !managerId) {
          apllyToast('warning', 'Selecione o gestor responsável!');
          scrollToField('manager');
          setLoading(false);
          return;
        }

        // Validar dados do relatório (data do gasto é controlada por estado)
        await reportSchema.validate(
          { ...data, date: reportDate },
          { abortEarly: false },
        );

        // Validar que há pelo menos um item
        if (items.length === 0) {
          apllyToast(
            'warning',
            'Adicione pelo menos uma despesa ao relatório!',
          );
          scrollToField('expense_type');
          setLoading(false);
          return;
        }

        // Itens restaurados de um rascunho perdem o arquivo do comprovante
        // (não é possível persistir File no localStorage) — bloqueia o envio
        // até o usuário reanexar, para não mandar como se tivesse comprovante.
        const pendingReceiptItem = items.find(
          it =>
            (it as IWizardExpenseItem).receiptPending &&
            RECEIPT_REQUIRED_TYPES.includes(it.expense_type),
        );
        if (pendingReceiptItem) {
          apllyToast(
            'warning',
            'Reanexe o comprovante da despesa restaurada do rascunho antes de salvar.',
          );
          setLoading(false);
          return;
        }

        // Validar rateio — a soma nunca pode ultrapassar o total das despesas
        const total = items.reduce((sum, i) => sum + (i.amount || 0), 0);
        if (rateio.allocations.length > 0) {
          const distributed = computeRateioDistributed(
            rateio,
            total,
            rateioItems,
          );
          if (distributed > total + 0.001) {
            apllyToast(
              'warning',
              'O rateio ultrapassa o total das despesas. Ajuste as alocações.',
            );
            scrollToField('rateio');
            setLoading(false);
            return;
          }
        }

        // Se o usuário optou por CC por despesa, monta rateio modo 'itens'
        // agrupando itens por centro de custo.
        let effectiveRateio =
          rateio.allocations.length > 0 ? rateio : undefined;
        if (costCenterPerItem) {
          const missing = items.some(it => !itemCostCenters[it.id]);
          if (missing) {
            apllyToast(
              'warning',
              'Defina o centro de custo de todas as despesas ou desative a opção por despesa.',
            );
            setLoading(false);
            return;
          }
          const byCC = new Map<string, string[]>(); // cost_center -> item_ids
          items.forEach(it => {
            const cc = itemCostCenters[it.id];
            if (cc) {
              const arr = byCC.get(cc) || [];
              arr.push(it.id);
              byCC.set(cc, arr);
            }
          });
          if (byCC.size > 0) {
            effectiveRateio = {
              mode: 'itens',
              allocations: Array.from(byCC.entries()).map(
                ([cost_center, item_ids]) => ({
                  cost_center,
                  item_ids,
                }),
              ),
            };
          }
        }

        // Espelhar dados do responsável nos campos legados do relatório
        const reportData: Omit<
          IExpenseReport,
          'id' | 'created_at' | 'updated_at'
        > = {
          director: responsible.user_id,
          manager_id: managerId,
          branches_id: responsible.branch_id || undefined,
          date: reportDate,
          sector: responsible.sector,
          section: responsible.section,
          // Com CC por despesa, o rateio é a fonte de verdade — o CC único do
          // relatório vira um marcador ('MÚLTIPLOS') para o lançamento contábil
          // não receber um centro de custo conflitante.
          cost_center: costCenterPerItem ? 'MÚLTIPLOS' : responsible.cost_center,
          // Enviar a string tal como está (mesmo vazia) — usar `|| undefined`
          // aqui faria um campo limpo pelo usuário virar `undefined` e ser
          // removido do JSON, então o backend nunca recebia a limpeza.
          pix_key: responsible.pix_key,
          bank_name: responsible.bank_name,
          bank_agency: responsible.bank_agency,
          bank_account: responsible.bank_account,
          participants,
          rateio: effectiveRateio,
          items,
          // Vencimento manual: só se aplica à Ajuda de custo, cujo prazo é
          // definido por quem solicita (não calculado pelo backend).
          due_date: isAjudaCustoReport(items) ? ajudaCustoDueDate || undefined : undefined,
          signatureStatus: 'pending',
        };

        handleAddExpense(reportData);
        if (user?.id) {
          clearExpenseReportDraft(user.id);
        }
        onClose();
        resetForm();
        setLoading(false);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          // Scroll ao primeiro campo com erro
          const firstErrorField = Object.keys(errors)[0];
          if (firstErrorField) {
            scrollToField(firstErrorField);
            apllyToast('warning', Object.values(errors)[0]);
          }
        } else {
          apllyToast('warning', 'Problemas ao salvar relatório!');
        }
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, steps, reportDate, responsible, participants, managerId, rateio, rateioItems, items, costCenterPerItem, itemCostCenters, ajudaCustoDueDate, user?.id, handleAddExpense, onClose, resetForm],
  );

  const { containerProps, indicatorEl } = useLoading({
    loading,
    indicator: <Oval />,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getUserName = (id?: string) =>
    users.find(u => u.id === id)?.name || '—';

  // Ajuda de custo é sempre o único item do relatório — não faz sentido
  // ratear entre centros de custo, então a etapa de Rateio é ocultada.
  const isAjudaCusto = isAjudaCustoReport(items);

  // Definição das etapas do wizard (dinâmica: Rateio some para Ajuda de custo)
  const steps = useMemo(() => {
    const allSteps = [
      { id: 'participants', title: 'Participantes', icon: RiUserLine },
      { id: 'expenses', title: 'Despesas', icon: RiMoneyDollarCircleLine },
      { id: 'rateio', title: 'Rateio', icon: RiPieChartLine },
      { id: 'review', title: 'Revisão', icon: RiCheckLine },
    ] as const;
    return isAjudaCusto
      ? allSteps.filter(s => s.id !== 'rateio')
      : allSteps;
  }, [isAjudaCusto]);

  const currentStepId = steps[step]?.id;

  // Garante que a etapa atual continue válida se a lista de etapas encolher
  // (ex: usuário adiciona Ajuda de custo enquanto está numa etapa posterior).
  useEffect(() => {
    setStep(s => Math.min(s, steps.length - 1));
  }, [steps.length]);

  // Valida a etapa atual antes de avançar
  const validateStep = (index: number): boolean => {
    const stepId = steps[index]?.id;
    if (stepId === 'participants') {
      if (!responsible || !responsible.user_id) {
        apllyToast('warning', 'Selecione o usuário responsável!');
        return false;
      }
      const incomplete = participants.find(
        p =>
          !p.user_id ||
          !p.sector?.trim() ||
          !p.section?.trim() ||
          !p.cost_center?.trim(),
      );
      if (incomplete) {
        apllyToast(
          'warning',
          'Preencha usuário, setor, seção e centro de custo de todos os participantes!',
        );
        return false;
      }
      if (!isDirectorUser && !managerId) {
        apllyToast('warning', 'Selecione o gestor responsável!');
        return false;
      }
      if (!isValidReportDate(reportDate)) {
        apllyToast('warning', 'Informe uma data válida para o gasto!');
        return false;
      }
      return true;
    }

    if (stepId === 'expenses') {
      if (items.length === 0) {
        apllyToast('warning', 'Adicione pelo menos uma despesa ao relatório!');
        return false;
      }
      return true;
    }

    if (stepId === 'rateio') {
      if (rateio.allocations.length > 0) {
        const distributed = computeRateioDistributed(
          rateio,
          expensesTotal,
          rateioItems,
        );
        if (distributed > expensesTotal + 0.001) {
          apllyToast(
            'warning',
            'O rateio ultrapassa o total das despesas. Ajuste as alocações.',
          );
          return false;
        }
      }
      return true;
    }

    return true;
  };

  const goNext = () => {
    if (validateStep(step)) {
      setStep(s => Math.min(s + 1, steps.length - 1));
    }
  };

  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const handleCloseModal = () => {
    // Grava o rascunho com o estado atual ANTES de resetForm() apagá-lo —
    // sem isso, fechar menos de 500ms depois da última edição cancelava o
    // autosave (debounce) sem nunca ter chegado a salvar aquela edição.
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
      draftSaveTimeoutRef.current = null;
    }
    flushDraftSave();
    onClose();
    resetForm();
  };

  return (
    <Box display="inline">
      {/* ts-expect-error omitted as it is unused currently */}
      <Button
        title="Novo Relatório de Despesas"
        size="sm"
        bg="#1a5490"
        color="white"
        fontWeight="medium"
        onClick={onOpen}
        cursor="pointer"
        leftIcon={<Icon as={RiAddLine} fontSize="18" />}
        boxShadow="sm"
        _hover={{
          bg: '#14447a',
          boxShadow: 'md',
          transform: 'translateY(-1px)',
        }}
        _active={{
          bg: '#0f3460',
          transform: 'translateY(0)',
        }}
        transition="all 0.2s"
      >
        {isWideVersion && 'Novo Relatório'}
      </Button>

      <Alert
        isOpen={showDraftPrompt}
        onClose={handleContinueDraft}
        handleFunction={handleDiscardDraft}
        title="Rascunho encontrado"
        textBody="Você tem um rascunho não finalizado deste relatório. Deseja continuar de onde parou ou descartar e começar um novo?"
        ButonClose="Continuar"
        ButonSave="Descartar"
      />

      <ModalComponent
        title="Novo Relatório de Despesas e Reembolsos"
        isOpen={isOpen}
        onClose={handleCloseModal}
        sizeSpecifyc="4xl"
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalBody pb={6} maxH="80vh" overflowY="auto" color="#1a202c">
          {/* Indicador de etapas */}
          <Flex align="flex-start" justify="space-between" mb={8} px={1}>
            {steps.map((s, i) => (
              <Flex
                key={s.title}
                align="center"
                flex={i < steps.length - 1 ? 1 : '0 0 auto'}
              >
                <Flex direction="column" align="center" minW="68px">
                  <Flex
                    align="center"
                    justify="center"
                    w="38px"
                    h="38px"
                    borderRadius="full"
                    bg={(() => {
                      if (i === step) return '#1a5490';
                      if (i < step) return '#16a34a';
                      return '#e2e8f0';
                    })()}
                    color={i <= step ? 'white' : '#94a3b8'}
                    boxShadow={
                      i === step ? '0 0 0 4px rgba(26,84,144,0.15)' : 'none'
                    }
                    transition="all 0.2s"
                  >
                    <Icon
                      as={i < step ? RiCheckboxCircleFill : s.icon}
                      fontSize="18"
                    />
                  </Flex>
                  <Text
                    mt={1}
                    fontSize="xs"
                    fontWeight={i === step ? '700' : '500'}
                    color={i === step ? '#1a5490' : '#64748b'}
                    textAlign="center"
                  >
                    {s.title}
                  </Text>
                </Flex>
                {i < steps.length - 1 && (
                  <Box
                    flex={1}
                    h="2px"
                    mx={2}
                    mt="-18px"
                    borderRadius="full"
                    bg={i < step ? '#16a34a' : '#e2e8f0'}
                  />
                )}
              </Flex>
            ))}
          </Flex>

          <Form ref={formRef} onSubmit={handleSubmit}>
            {/* ETAPA 1: Participantes, Gestor e Data */}
            {currentStepId === 'participants' && (
            <Box
              bg="#f8fafc"
              p={6}
              borderRadius="lg"
              border="1px solid"
              borderColor="#e2e8f0"
              boxShadow="sm"
              color="#1a202c"
            >
              <HStack spacing={2} mb={4}>
                <Icon as={RiFileTextLine} fontSize="20" color="#1a5490" />
                <Text fontWeight="bold" fontSize="lg" color="#1a5490">
                  Dados do Relatório
                </Text>
              </HStack>

              <VStack spacing={4} align="stretch">
                <Box data-field="participants">
                  <HStack spacing={2} mb={2}>
                    <Icon as={RiUserLine} fontSize="16" />
                    <Text fontSize="sm" fontWeight="600">
                      Participantes (Responsável e Vinculados)
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="#64748b" mb={3}>
                    Cada participante possui seu próprio setor, seção, centro de
                    custo e filial. O pagamento de cada vinculado é opcional —
                    desative quando o responsável arcou com a despesa.
                  </Text>
                  <ExpenseParticipants
                    participants={participants}
                    onChange={setParticipants}
                    users={users}
                    branches={branches}
                  />
                </Box>

                <Box data-field="manager">
                  <HStack spacing={2} mb={2}>
                    <Icon as={RiUserLine} fontSize="16" />
                    <Text fontSize="sm" fontWeight="600">
                      Gestor Responsável (aprovação)
                    </Text>
                  </HStack>
                  <UserSelect
                    users={users.filter(
                      u => !participants.some(p => p.user_id === u.id),
                    )}
                    value={managerId}
                    onChange={setManagerId}
                    placeholder="Pesquisar e selecionar gestor"
                  />
                </Box>

                <Box>
                  <HStack spacing={2} mb={2}>
                    <Icon as={RiCalendarLine} fontSize="16" />
                    <Text fontSize="sm" fontWeight="600">
                      Data do Gasto
                    </Text>
                  </HStack>
                  <ChakraInput
                    type="date"
                    min={MIN_REPORT_DATE}
                    max={maxReportDate()}
                    value={reportDate}
                    onChange={e => setReportDate(e.target.value)}
                    color="#1a202c"
                    borderColor="#cbd5e1"
                    bg="white"
                  />
                </Box>

                <Box>
                  <HStack spacing={2} mb={2}>
                    <Icon as={RiMapPinLine} fontSize="16" />
                    <Text fontSize="sm" fontWeight="600">
                      Centro de Custo
                    </Text>
                  </HStack>
                  <HStack
                    p={3}
                    bg="#f1f5f9"
                    borderRadius="md"
                    borderLeft="3px solid"
                    borderLeftColor="#1a5490"
                    spacing={3}
                    align="flex-start"
                  >
                    <Switch
                      size="sm"
                      mt="2px"
                      isChecked={costCenterPerItem}
                      onChange={e => handleTogglePerItem(e.target.checked)}
                      colorScheme="blue"
                    />
                    <Box>
                      <Text fontSize="sm" fontWeight="600" color="#1e293b">
                        Definir centro de custo por despesa
                      </Text>
                      <Text fontSize="xs" color="#64748b">
                        Ativado: cada despesa terá seu próprio centro de custo (na
                        etapa Despesas). Desativado: usa o centro de custo do
                        responsável para todo o relatório.
                      </Text>
                    </Box>
                  </HStack>
                </Box>

                <Box
                  mt={2}
                  p={4}
                  bg="#e0f2fe"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="#1a5490"
                >
                  <HStack spacing={2} mb={2}>
                    <Icon as={RiInformationLine} fontSize="16" color="#1a5490" />
                    <Text fontSize="sm" fontWeight="600" color="#1a5490">
                      Assinatura Digital
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="#334155" lineHeight="1.6">
                    A assinatura digital será solicitada através de email para o
                    usuário, com autenticação via gov.br (ICP-Brasil).
                  </Text>
                </Box>
              </VStack>
            </Box>
            )}

            {/* ETAPA 2: Despesas (lista + nova despesa)
                Mantida sempre montada (só oculta via display) — diferente das
                outras etapas, aqui o formulário de "nova despesa" guarda
                Valor/Comprovante/Observações direto no DOM via Unform, sem
                estado React. Desmontar ao voltar para Participantes e
                remontar ao avançar de novo apagava esses campos mesmo com o
                tipo de despesa continuando selecionado (que fica em estado). */}
            <Box display={currentStepId === 'expenses' ? 'block' : 'none'}>
              <VStack align="stretch" spacing={6}>
                <Box>
                  <HStack spacing={2} mb={4}>
                    <Icon
                      as={RiMoneyDollarCircleLine}
                      fontSize="20"
                      color="#1a5490"
                    />
                    <Text fontWeight="bold" fontSize="lg" color="#1a5490">
                      Despesas Adicionadas
                    </Text>
                    <Badge
                      ml={2}
                      bg={items.length > 0 ? '#1a5490' : '#94a3b8'}
                      color="white"
                      fontSize="xs"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontWeight="600"
                    >
                      {items.length} {items.length === 1 ? 'item' : 'itens'}
                    </Badge>
                  </HStack>
  
                  {costCenterPerItem && (
                    <HStack
                      mb={4}
                      p={3}
                      bg="#e0f2fe"
                      borderRadius="md"
                      borderLeft="3px solid"
                      borderLeftColor="#1a5490"
                      spacing={2}
                    >
                      <Icon
                        as={RiInformationLine}
                        fontSize="16"
                        color="#1a5490"
                      />
                      <Text fontSize="xs" color="#1e293b">
                        Centro de custo por despesa ativado — defina o centro de
                        custo de cada item abaixo.
                      </Text>
                    </HStack>
                  )}
  
                  {items.length === 0 ? (
                    <Box
                      p={8}
                      bg="white"
                      borderRadius="lg"
                      textAlign="center"
                      borderWidth={2}
                      borderStyle="dashed"
                      borderColor="#cbd5e1"
                    >
                      <Icon
                        as={RiFileTextLine}
                        fontSize="48"
                        color="#94a3b8"
                        mb={3}
                      />
                      <Text color="#64748b" fontSize="sm" fontWeight="500">
                        Nenhuma despesa adicionada ainda
                      </Text>
                      <Text color="#94a3b8" fontSize="xs" mt={1}>
                        Adicione despesas usando o formulário abaixo
                      </Text>
                    </Box>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {items.map(item => (
                      <Box
                        key={item.id}
                        p={4}
                        bg="white"
                        color="#1a202c"
                        borderWidth={1}
                        borderColor="#e2e8f0"
                        borderRadius="lg"
                        boxShadow="sm"
                        transition="all 0.2s"
                        _hover={{
                          boxShadow: 'md',
                          borderColor: '#1a5490',
                          transform: 'translateY(-1px)',
                        }}
                      >
                        <HStack justify="space-between">
                          <HStack spacing={4} flex={1}>
                            <Badge
                              colorScheme={getExpenseColor(item.expense_type)}
                              px={3}
                              py={1}
                              borderRadius="md"
                              fontSize="xs"
                              fontWeight="600"
                            >
                              {item.expense_type}
                            </Badge>
                            <Text fontSize="md" fontWeight="600">
                              {item.expense_type === 'Quilometragem adicional'
                              ? `${item.additional_km} km`
                              : formatCurrency(item.amount || 0)}
                            </Text>
                            {item.displacement_reason && (
                            <Text fontSize="sm" color="gray.500" isTruncated>
                              {item.displacement_reason}
                            </Text>
                          )}
                            {item.observations ? (
                              <Text fontSize="sm" color="gray.500" isTruncated>
                                {(() => {
                                if (item.expense_type === 'Ajuda de custo') {
                                  try {
                                    const parsed = JSON.parse(item.observations);
                                    return `Evento: ${parsed.nome || 'N/A'}`;
                                  } catch (e) {
                                    return item.observations;
                                  }
                                }
                                if (item.expense_type === 'Quilometragem adicional') {
                                  try {
                                    const parsed = JSON.parse(item.observations);
                                    return parsed.obs ? `Obs: ${parsed.obs}` : '';
                                  } catch (e) {
                                    return item.observations;
                                  }
                                }
                                return item.observations;
                              })()}
                              </Text>
                          ) : null}
                          </HStack>
                          <IconButton
                            aria-label="Remover item"
                            icon={<RiDeleteBinLine />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleRemoveItem(item.id)}
                          />
                        </HStack>
                        {(item as IWizardExpenseItem).receiptPending && (
                          <Box
                            mt={3}
                            p={3}
                            bg="orange.50"
                            borderRadius="md"
                            borderLeft="3px solid"
                            borderLeftColor="orange.400"
                          >
                            <Text fontSize="xs" fontWeight="600" color="orange.700" mb={2}>
                              ⚠ Rascunho restaurado — reanexe o comprovante desta despesa
                              {(item as IWizardExpenseItem).receiptFileName
                                ? ` (antes: ${(item as IWizardExpenseItem).receiptFileName})`
                                : ''}
                            </Text>
                            <ChakraInput
                              size="sm"
                              type="file"
                              accept="image/jpeg,image/png,image/jpg,application/pdf"
                              bg="white"
                              onChange={e =>
                                handleReattachReceipt(item.id, e.target.files?.[0] || null)}
                            />
                          </Box>
                        )}
                        {costCenterPerItem && (
                          <Box mt={3}>
                            <Text fontSize="xs" color="#64748b" mb={1}>
                              Centro de custo desta despesa
                            </Text>
                            <SearchableSelect
                              options={costCenterOptions}
                              value={itemCostCenters[item.id] || ''}
                              onChange={value =>
                                handleItemCostCenterChange(item.id, value)}
                              placeholder="Centro de custo desta despesa"
                              borderColor="#cbd5e1"
                              emptyMessage="Nenhum centro de custo"
                            />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </VStack>
                )}
                </Box>
  
                {/* Formulário: adicionar nova despesa */}
                <Form ref={itemFormRef} onSubmit={handleAddItem}>
                  <Box
                    bg="#f0fdf4"
                    p={6}
                    borderRadius="lg"
                    mb={4}
                    border="1px solid"
                    borderColor="#bbf7d0"
                    boxShadow="sm"
                  >
                    <HStack spacing={2} mb={4}>
                      <Icon as={RiAddLine} fontSize="20" color="#16a34a" />
                      <Text fontWeight="bold" fontSize="lg" color="#15803d">
                        Nova Despesa ou Ajuda de Custo
                      </Text>
                    </HStack>
  
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <HStack spacing={2} mb={2}>
                          <Icon as={RiFileTextLine} fontSize="16" />
                          <Text fontSize="sm" fontWeight="600">
                            Tipo de Despesa
                          </Text>
                        </HStack>
                        <Select
                          name="item_expense_type"
                          label=""
                          value={currentItemType}
                          onChange={e => handleItemTypeChange(e.target.value)}
                          options={[
                          { value: 'Refeição', label: 'Refeição' },
                          {
                            value: 'Quilometragem adicional',
                            label: 'Quilometragem adicional',
                          },
                          {
                            value: 'Pedágio adicional',
                            label: 'Pedágio adicional',
                          },
                          { value: 'Ajuda de custo', label: 'Ajuda de custo' },
                          { value: 'Outros', label: 'Outros' },
                        ]}
                        />
                      </Box>
  
                      {showAmountField && (
                      <Box>
                        <HStack spacing={2} mb={2}>
                          <Icon
                            as={RiMoneyDollarCircleLine}
                            fontSize="16"
  
                          />
                          <Text fontSize="sm" fontWeight="600">
                            Valor (R$)
                          </Text>
                        </HStack>
                        <Input
                          type="number"
                          step="0.01"
                          name="item_amount"
                          placeholder="Ex: 45.90"
                        />
                        {currentItemType === 'Refeição' && (
                          <Text fontSize="xs" color="green.600" mt={1}>
                            💡 Valor sugerido até R$ 70,00
                          </Text>
                        )}
                      </Box>
                    )}
  
                      {showKmFields && (
                      <>
                        {/* KM Inicial e Final */}
                        <Box
                          p={4}
                          bg="#f0fdf4"
                          borderRadius="md"
                          borderLeft="3px solid"
                          borderLeftColor="#16a34a"
                        >
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="#166534"
                            mb={3}
                          >
                            Hodômetro
                          </Text>
                          <HStack spacing={4}>
                            <Box flex={1}>
                              <Text fontSize="xs" color="#64748b" mb={1}>
                                KM Inicial (Saída)
                              </Text>
                              <ChakraInput
                                size="md"
                                type="number"
                                step="1"
                                placeholder="Ex: 45000"
                                value={kmStart}
                                onChange={e => {
                                  setKmStart(e.target.value);
                                }}
                                color="#1a202c"
                                borderColor="#cbd5e1"
                              />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="xs" color="#64748b" mb={1}>
                                KM Final (Chegada)
                              </Text>
                              <ChakraInput
                                size="md"
                                type="number"
                                step="1"
                                placeholder="Ex: 45150"
                                value={kmEnd}
                                onChange={e => {
                                  setKmEnd(e.target.value);
                                }}
                                color="#1a202c"
                                borderColor="#cbd5e1"
                              />
                            </Box>
                          </HStack>
  
                          {/* KM Calculado */}
                          {calculatedKm > 0 && (
                            <Box
                              mt={3}
                              p={2}
                              bg="white"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="#bbf7d0"
                            >
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="#166534">
                                  KM Percorrido:
                                </Text>
                                <Text
                                  fontSize="lg"
                                  fontWeight="bold"
                                  color="#166534"
                                >
                                  {calculatedKm} km
                                </Text>
                              </HStack>
                              {kmHasDecimals && (
                                <Text fontSize="xs" color="#64748b" mt={1}>
                                  O hodômetro será registrado arredondado:{' '}
                                  {kmStartValue} → {kmEndValue}
                                </Text>
                              )}
                            </Box>
                          )}
  
                          {calculatedKm < 0 && (
                            <Text fontSize="xs" color="red.500" mt={2}>
                              KM Final deve ser maior que KM Inicial
                            </Text>
                          )}
  
                          {/* Fotos do Hodômetro */}
                          <HStack spacing={4} mt={3}>
                            <Box flex={1}>
                              <Text fontSize="xs" color="#64748b" mb={1}>
                                📷 Foto Hodômetro - Saída
                              </Text>
                              <ChakraInput
                                size="sm"
                                type="file"
                                accept="image/jpeg,image/png,image/jpg"
                                onChange={e => {
                                  setKmStartPhoto(
                                    e.target.files?.[0] || null,
                                  );
                                }}
                                p={1}
                                color="#1a202c"
                                borderColor="#cbd5e1"
                              />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="xs" color="#64748b" mb={1}>
                                📷 Foto Hodômetro - Chegada
                              </Text>
                              <ChakraInput
                                size="sm"
                                type="file"
                                accept="image/jpeg,image/png,image/jpg"
                                onChange={e => {
                                  setKmEndPhoto(
                                    e.target.files?.[0] || null,
                                  );
                                }}
                                p={1}
                                color="#1a202c"
                                borderColor="#cbd5e1"
                              />
                            </Box>
                          </HStack>
  
                          {canUseFixedKmRate && (
                            <HStack
                              mt={3}
                              p={2}
                              bg="#e0f2fe"
                              borderRadius="md"
                              borderLeft="3px solid"
                              borderLeftColor="#0284c7"
                              spacing={3}
                            >
                              <Switch
                                size="sm"
                                isChecked={useDirectorKmRate}
                                onChange={e => {
                                  setUseDirectorKmRate(e.target.checked);
                                }}
                                colorScheme="blue"
                              />
                              <Text fontSize="xs" color="#1e293b">
                                {useDirectorKmRate
                                  ? `Tabela Fixa (R$ ${SPECIAL_SECTOR_RATE.toFixed(2)}/km)`
                                  : 'Tabela de KM padrão (por faixa)'}
                              </Text>
                            </HStack>
                          )}
                        </Box>
  
                        {/* Campo hidden para o unform validar */}
                        <Box display="none">
                          <Input
                            type="number"
                            name="item_additional_km"
                            value={String(
                              calculatedKm > 0 ? calculatedKm : '',
                            )}
                          />
                        </Box>
  
                        <Box>
                          <HStack spacing={2} mb={2}>
                            <Icon as={RiMapPinLine} fontSize="16" />
                            <Text fontSize="sm" fontWeight="600">
                              Motivo do Deslocamento
                            </Text>
                          </HStack>
                          <TextArea
                            name="item_displacement_reason"
                            placeholder="Descreva o motivo do deslocamento adicional..."
                          />
                        </Box>
                      </>
                    )}
  
                      {showReceiptField && (
                      <Box>
                        <HStack spacing={2} mb={2}>
                          <Icon as={RiFileUploadLine} fontSize="16" />
                          <Text fontSize="sm" fontWeight="600">
                            Comprovante
                          </Text>
                        </HStack>
                        <UploadSimulated name="item_receipt" />
                      </Box>
                    )}
  
                      {showObservationsField && (
                      <Box>
                        <HStack spacing={2} mb={2}>
                          <Icon as={RiFileTextLine} fontSize="16" />
                          <Text fontSize="sm" fontWeight="600" color="gray.700">
                            Observações
                          </Text>
                        </HStack>
                        <TextArea
                          name="item_observations"
                          placeholder="Descreva detalhadamente a despesa..."
                        />
                      </Box>
                    )}
  
                      {showAjudaCustoFields && (
                      <Box
                        p={4}
                        bg="white"
                        borderRadius="md"
                        borderLeft="3px solid"
                        borderColor="teal.500"
                        boxShadow="sm"
                      >
                        <Box data-field="ajuda_custo_data_pagamento" mb={4}>
                          <HStack spacing={2} mb={2}>
                            <Icon as={RiCalendarLine} fontSize="16" color="teal.700" />
                            <Text fontSize="sm" fontWeight="bold" color="teal.700">
                              Data de Pagamento Desejada
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.600" mb={2}>
                            Escolha a data em que você precisa receber o valor da ajuda de custo.
                          </Text>
                          <ChakraInput
                            type="date"
                            value={ajudaCustoDueDate}
                            onChange={e => setAjudaCustoDueDate(e.target.value)}
                            color="#1a202c"
                            borderColor="#cbd5e1"
                            bg="white"
                          />
                        </Box>
  
                        <Divider my={4} borderColor="gray.300" />
  
                        <Text fontSize="sm" fontWeight="bold" color="teal.700" mb={3}>
                          DADOS DO EVENTO
                        </Text>
  
                        <VStack spacing={3} align="stretch" mb={4}>
                          <Box>
                            <Text fontSize="xs" fontWeight="600" mb={1}>Nome:</Text>
                            <Input name="ajuda_custo_nome" placeholder="Ex: Conferência Anual" />
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="600" mb={1}>Objetivo:</Text>
                            <Input name="ajuda_custo_objetivo" placeholder="Ex: Networking e parcerias" />
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="600" mb={1}>Local do evento:</Text>
                            <Input name="ajuda_custo_local" placeholder="Ex: Centro de Convenções" />
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="600" mb={1}>Endereço:</Text>
                            <Input name="ajuda_custo_endereco" placeholder="Ex: Av. Paulista, 1000" />
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="600" mb={1}>Período do evento (Início e Fim):</Text>
                            <Input name="ajuda_custo_periodo" placeholder="Ex: Sexta-feira (13/03) a sábado (14/03)" />
                          </Box>
  
                          <Text fontSize="xs" fontWeight="600" mt={2} mb={1}>Horários do Evento:</Text>
                          {eventSchedules.map((schedule, index) => (
                            <HStack spacing={4} key={schedule.id} align="flex-end">
                              <Box flex={1}>
                                <Text fontSize="xs" fontWeight="600" mb={1}>Horário de Início:</Text>
                                <Input name={`ajuda_custo_horario_inicio_${index}`} placeholder="Ex: 08h" />
                              </Box>
                              <Box flex={1}>
                                <Text fontSize="xs" fontWeight="600" mb={1}>Horário de Término:</Text>
                                <Input name={`ajuda_custo_horario_termino_${index}`} placeholder="Ex: 17h30" />
                              </Box>
                              {eventSchedules.length > 1 && (
                                <IconButton
                                  aria-label="Remover horário"
                                  icon={<RiDeleteBinLine />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => setEventSchedules(prev => prev.filter(s => s.id !== schedule.id))}
                                />
                              )}
                            </HStack>
                          ))}
                          <Button
                            size="sm"
                            alignSelf="flex-start"
                            variant="outline"
                            colorScheme="teal"
                            leftIcon={<Icon as={RiAddLine} />}
                            onClick={() => setEventSchedules(prev => [...prev, { id: uuid() }])}
                          >
                            Adicionar Horário
                          </Button>
                        </VStack>
  
                        <Divider my={4} borderColor="gray.300" />
  
                        <Text fontSize="sm" fontWeight="bold" color="teal.700" mb={3}>
                          DADOS DA VIAGEM
                        </Text>
  
                        <VStack spacing={3} align="stretch">
                          <Box>
                            <Text fontSize="xs" fontWeight="600" mb={1}>Data da viagem:</Text>
                            <Input type="date" name="ajuda_custo_data_viagem" />
                          </Box>
  
                          <HStack spacing={4}>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="600" mb={1}>Trecho de Ida:</Text>
                              <Input name="ajuda_custo_trecho_ida" placeholder="Ex: SP -> RJ" />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="600" mb={1}>Data (Ida):</Text>
                              <Input type="date" name="ajuda_custo_trecho_ida_data" />
                            </Box>
                          </HStack>
  
                          <HStack spacing={4}>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="600" mb={1}>Trecho de Volta:</Text>
                              <Input name="ajuda_custo_trecho_volta" placeholder="Ex: RJ -> SP" />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="600" mb={1}>Data (Volta):</Text>
                              <Input type="date" name="ajuda_custo_trecho_volta_data" />
                            </Box>
                          </HStack>
                        </VStack>
  
                        <Divider my={4} borderColor="gray.300" />
  
                        <Text fontSize="sm" fontWeight="bold" color="teal.700" mb={3}>
                          DESCRIÇÃO DE VALORES
                        </Text>
                        <Box data-field="ajuda_custo_descricao_valor">
                          <Text fontSize="xs" fontWeight="600" mb={1}>
                            Justificativa para solicitação da ajuda de custo:
                          </Text>
                          <TextArea
                            name="ajuda_custo_descricao_valor"
                            placeholder="Ex: 2 jantas e 3 almoços"
                          />
                        </Box>
                      </Box>
                    )}
  
                      {currentItemType && (
                      <Button
                        colorScheme="green"
                        size="md"
                        width="100%"
                        mt={2}
                        type="button"
                        onClick={() => itemFormRef.current?.submitForm()}
                        leftIcon={<Icon as={RiAddLine} />}
                        boxShadow="sm"
                        _hover={{ boxShadow: 'md' }}
                      >
                        Adicionar Despesa
                      </Button>
                    )}
                    </VStack>
                  </Box>
                </Form>
              </VStack>
            </Box>

            {/* ETAPA 3: Rateio por Centro de Custo (não se aplica a Ajuda de custo) */}
            {currentStepId === 'rateio' && (
              <Box data-field="rateio">
                <HStack spacing={2} mb={4}>
                  <Icon as={RiPieChartLine} fontSize="20" color="#1a5490" />
                  <Text fontWeight="bold" fontSize="lg" color="#1a5490">
                    Rateio por Centro de Custo
                  </Text>
                </HStack>
                <Text fontSize="xs" color="#64748b" mb={4}>
                  Opcional. Distribua o total das despesas entre os centros de
                  custo. A soma não pode ultrapassar {formatCurrency(expensesTotal)}.
                </Text>
                <ExpenseRateio
                  rateio={rateio}
                  onChange={setRateio}
                  total={expensesTotal}
                  items={rateioItems}
                  participantCostCenters={participants.map(p => ({
                    cost_center: p.cost_center,
                    sector: p.sector,
                    section: p.section,
                  }))}
                />
              </Box>
            )}

            {/* ETAPA 4: Revisão */}
            {currentStepId === 'review' && (
              <VStack align="stretch" spacing={5}>
                <Box
                  bg="#f8fafc"
                  p={5}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="#e2e8f0"
                >
                  <HStack spacing={2} mb={4}>
                    <Icon as={RiCheckLine} fontSize="20" color="#1a5490" />
                    <Text fontWeight="bold" fontSize="lg" color="#1a5490">
                      Revisão do Relatório
                    </Text>
                  </HStack>
                  <HStack spacing={10} flexWrap="wrap">
                    <Box>
                      <Text fontSize="xs" color="#64748b">
                        Data do gasto
                      </Text>
                      <Text fontWeight="600">
                        {formatReportDate(reportDate, 'dd/MM/yyyy')}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="#64748b">
                        Gestor
                      </Text>
                      <Text fontWeight="600">{getUserName(managerId)}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="#64748b">
                        Total
                      </Text>
                      <Text fontWeight="700" color="#16a34a">
                        {formatCurrency(expensesTotal)}
                      </Text>
                    </Box>
                  </HStack>
                </Box>

                <Box>
                  <Text fontWeight="600" mb={2} color="#334155">
                    Participantes ({participants.length})
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {participants.map(p => (
                      <HStack
                        key={p.id || p.user_id}
                        p={3}
                        bg="white"
                        border="1px solid"
                        borderColor="#e2e8f0"
                        borderRadius="md"
                        justify="space-between"
                        flexWrap="wrap"
                      >
                        <HStack spacing={3} flexWrap="wrap">
                          <Badge
                            colorScheme={
                              p.role === 'responsible' ? 'blue' : 'gray'
                            }
                          >
                            {p.role === 'responsible'
                              ? 'Responsável'
                              : 'Vinculado'}
                          </Badge>
                          <Text fontWeight="600">{getUserName(p.user_id)}</Text>
                          <Text fontSize="sm" color="#64748b">
                            {p.sector}/{p.section} • {p.cost_center}
                          </Text>
                        </HStack>
                        <Badge
                          colorScheme={p.payment_required ? 'green' : 'orange'}
                        >
                          {p.payment_required ? 'Pagamento' : 'Sem pagamento'}
                        </Badge>
                      </HStack>
                    ))}
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="600" mb={2} color="#334155">
                    Despesas ({items.length})
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {items.map(item => (
                      <HStack
                        key={item.id}
                        p={3}
                        bg="white"
                        border="1px solid"
                        borderColor="#e2e8f0"
                        borderRadius="md"
                        justify="space-between"
                      >
                        <Badge colorScheme={getExpenseColor(item.expense_type)}>
                          {item.expense_type}
                        </Badge>
                        <Text fontWeight="600">
                          {item.expense_type === 'Quilometragem adicional'
                            ? `${item.additional_km} km`
                            : formatCurrency(item.amount || 0)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>

                {rateio.allocations.length > 0 && (
                  <Box>
                    <Text fontWeight="600" mb={2} color="#334155">
                      Rateio por Centro de Custo
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {rateio.allocations.map(a => (
                        <HStack
                          key={a.id}
                          p={3}
                          bg="white"
                          border="1px solid"
                          borderColor="#e2e8f0"
                          borderRadius="md"
                          justify="space-between"
                        >
                          <Text fontWeight="600">{a.cost_center || '—'}</Text>
                          <Text fontSize="sm" color="#64748b">
                            {a.sector || '—'}/{a.section || '—'}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                <Box
                  p={4}
                  bg="#e0f2fe"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="#1a5490"
                >
                  <HStack spacing={2} mb={1}>
                    <Icon
                      as={RiInformationLine}
                      fontSize="16"
                      color="#1a5490"
                    />
                    <Text fontSize="sm" fontWeight="600" color="#1a5490">
                      Assinatura Digital
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="#334155" lineHeight="1.6">
                    Ao salvar, a assinatura será solicitada por e-mail na ordem:
                    responsável → vinculados → gestor, com autenticação via
                    gov.br (ICP-Brasil).
                  </Text>
                </Box>
              </VStack>
            )}

            <ModalFooter
              borderTop="1px solid"
              borderTopColor="gray.200"
              pt={6}
              px={0}
            >
              <Flex w="100%" justify="space-between" align="center">
                <Button
                  variant="ghost"
                  onClick={handleCloseModal}
                  size="md"
                  _hover={{ bg: 'gray.100' }}
                >
                  Cancelar
                </Button>
                <HStack spacing={3}>
                  {step > 0 && (
                    <Button
                      variant="outline"
                      onClick={goBack}
                      size="md"
                      leftIcon={<Icon as={RiArrowLeftLine} />}
                    >
                      Voltar
                    </Button>
                  )}
                  {step < steps.length - 1 ? (
                    <Button
                      bg="#1a5490"
                      color="white"
                      onClick={goNext}
                      size="md"
                      px={6}
                      rightIcon={<Icon as={RiArrowRightLine} />}
                      _hover={{ bg: '#14447a' }}
                      _active={{ bg: '#0f3460' }}
                    >
                      Avançar
                    </Button>
                  ) : (
                    <Button
                      bg="#1a5490"
                      color="white"
                      type="submit"
                      isDisabled={items.length === 0}
                      size="md"
                      px={8}
                      boxShadow="md"
                      _hover={{ bg: '#14447a', boxShadow: 'lg' }}
                      _active={{ bg: '#0f3460' }}
                      _disabled={{
                        bg: 'gray.300',
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      }}
                    >
                      {loading ? (
                        <div {...containerProps} ref={componentRef}>
                          {indicatorEl}
                        </div>
                      ) : (
                        'Salvar Relatório'
                      )}
                    </Button>
                  )}
                </HStack>
              </Flex>
            </ModalFooter>
          </Form>
        </ModalBody>
      </ModalComponent>
    </Box>
  );
}
